import React, { useMemo } from 'react';
import classNames from 'classnames';
import { sortBy } from 'lodash';

import './matchGameResults.scss';

interface Cs2MatchGameResultsProps {
    gameNumber: number;
    matchGameResult: Cs2MatchGameResult;
}

export interface Cs2MatchGameResult {
    map?: string;
    roundsPlayed?: number;
    teams: Cs2MatchGameResultTeam[];
    rounds?: Cs2RoundResult[];
}

interface Cs2MatchGameResultTeam {
    score: number;
    participants: Cs2MatchGameResultParticipant[];
    teamId: string;
    winner: boolean;
}

interface Cs2MatchGameResultParticipant {
    steamId64: string;
    playerName: string;
    team: string;
    kills: number;
    deaths: number;
    assists: number;
    mvps: number;
    score: number;
    twoKs: number;
    threeKs: number;
    fourKs: number;
    fiveKs: number;
    killsWithHeadshot: number;
    killsWithPistol: number;
    killsWithSniper: number;
    damageDealt: number;
    entryAttempts: number;
    entrySuccesses: number;
    flashesThrown: number;
    flashesSuccessful: number;
    flashesEnemiesBlinded: number;
    utilityThrown: number;
    utilityDamage: number;
    oneVXAttempts: number;
    oneVXWins: number;
    kda: number;
    adr: number;
}

type RoundEndType =
    | 'elimination'
    | 'bomb_defused'
    | 'bomb_exploded'
    | 'time'
    | 'surrender'
    | 'unknown';

interface Cs2RoundResult {
    roundNumber?: number;
    winningTeamId?: string;
    teamId?: string;
    endType?: RoundEndType;
    winType?: RoundEndType;
    reason?: RoundEndType;
}

const MAP_IMAGE_BASE_URL = 'https://raw.githubusercontent.com/MurkyYT/cs2-map-icons/main/images';
const MAP_THUMBNAIL_BASE_URL = `${MAP_IMAGE_BASE_URL}/thumbs`;

const normalizeMapSlug = (mapName?: string) => mapName?.trim().toLowerCase().replace(/\s+/g, '_');

const getRoundOutcomeIcon = (endType: RoundEndType) => {
    switch (endType) {
        case 'elimination':
            return '☠';
        case 'bomb_defused':
            return '🛡';
        case 'bomb_exploded':
            return '💣';
        case 'time':
            return '⏱';
        case 'surrender':
            return '🏳';
        default:
            return '•';
    }
};

const getRoundOutcomeLabel = (endType: RoundEndType) => {
    switch (endType) {
        case 'elimination':
            return 'Won by elimination';
        case 'bomb_defused':
            return 'Won by defuse';
        case 'bomb_exploded':
            return 'Won by bomb explosion';
        case 'time':
            return 'Won by time';
        case 'surrender':
            return 'Won by surrender';
        default:
            return 'Round won';
    }
};

const MOCK_MATCH_GAME_RESULT: Cs2MatchGameResult = {
    map: 'de_mirage',
    roundsPlayed: 24,
    teams: [
        {
            teamId: 'team1',
            score: 13,
            winner: true,
            participants: [
                { steamId64: '111', playerName: 'Abarn', team: 'team1', kills: 24, deaths: 17, assists: 6, mvps: 5, score: 115, twoKs: 2, threeKs: 1, fourKs: 0, fiveKs: 0, killsWithHeadshot: 14, killsWithPistol: 4, killsWithSniper: 2, damageDealt: 2134, entryAttempts: 10, entrySuccesses: 5, flashesThrown: 9, flashesSuccessful: 4, flashesEnemiesBlinded: 6, utilityThrown: 21, utilityDamage: 176, oneVXAttempts: 2, oneVXWins: 1, kda: 1.76, adr: 89 },
                { steamId64: '112', playerName: 'ShiftPeek', team: 'team1', kills: 19, deaths: 15, assists: 5, mvps: 4, score: 98, twoKs: 3, threeKs: 0, fourKs: 0, fiveKs: 0, killsWithHeadshot: 11, killsWithPistol: 3, killsWithSniper: 1, damageDealt: 1844, entryAttempts: 8, entrySuccesses: 3, flashesThrown: 7, flashesSuccessful: 3, flashesEnemiesBlinded: 5, utilityThrown: 18, utilityDamage: 141, oneVXAttempts: 1, oneVXWins: 0, kda: 1.6, adr: 77 },
            ],
        },
        {
            teamId: 'team2',
            score: 11,
            winner: false,
            participants: [
                { steamId64: '221', playerName: 'Adam_H1', team: 'team2', kills: 21, deaths: 19, assists: 4, mvps: 3, score: 103, twoKs: 1, threeKs: 1, fourKs: 0, fiveKs: 0, killsWithHeadshot: 10, killsWithPistol: 2, killsWithSniper: 5, damageDealt: 1977, entryAttempts: 9, entrySuccesses: 4, flashesThrown: 6, flashesSuccessful: 2, flashesEnemiesBlinded: 3, utilityThrown: 15, utilityDamage: 124, oneVXAttempts: 3, oneVXWins: 1, kda: 1.32, adr: 82 },
                { steamId64: '222', playerName: 'HoldW', team: 'team2', kills: 15, deaths: 20, assists: 8, mvps: 2, score: 81, twoKs: 1, threeKs: 0, fourKs: 0, fiveKs: 0, killsWithHeadshot: 7, killsWithPistol: 1, killsWithSniper: 0, damageDealt: 1520, entryAttempts: 6, entrySuccesses: 2, flashesThrown: 10, flashesSuccessful: 5, flashesEnemiesBlinded: 8, utilityThrown: 20, utilityDamage: 158, oneVXAttempts: 1, oneVXWins: 0, kda: 1.15, adr: 63 },
            ],
        },
    ],
    rounds: [
        { roundNumber: 1, winningTeamId: 'team1', endType: 'elimination' },
        { roundNumber: 2, winningTeamId: 'team1', endType: 'bomb_exploded' },
        { roundNumber: 3, winningTeamId: 'team2', endType: 'bomb_defused' },
        { roundNumber: 4, winningTeamId: 'team1', endType: 'time' },
        { roundNumber: 5, winningTeamId: 'team2', endType: 'elimination' },
        { roundNumber: 6, winningTeamId: 'team1', endType: 'bomb_exploded' },
        { roundNumber: 7, winningTeamId: 'team1', endType: 'elimination' },
        { roundNumber: 8, winningTeamId: 'team2', endType: 'time' },
        { roundNumber: 9, winningTeamId: 'team1', endType: 'bomb_defused' },
        { roundNumber: 10, winningTeamId: 'team1', endType: 'elimination' },
    ],
};

const Cs2MatchGameResults = (props: Cs2MatchGameResultsProps) => {
    const { matchGameResult } = props;
    const isDevelopment = process.env.NODE_ENV === 'development';
    const hasRealResult = !!matchGameResult?.teams?.length;
    const displayResult = hasRealResult ? matchGameResult : isDevelopment ? MOCK_MATCH_GAME_RESULT : null;
    const isUsingMockData = !hasRealResult && isDevelopment;

    if (!displayResult) {
        return null;
    }

    const [team1, team2] = displayResult.teams;

    const teamById = useMemo(
        () => new Map(displayResult.teams.map((team) => [team.teamId, team] as const)),
        [displayResult.teams]
    );

    const mapArtwork = useMemo(() => {
        const mapSlug = normalizeMapSlug(displayResult.map);
        if (!mapSlug) return null;

        return {
            thumbnailUrl: `${MAP_THUMBNAIL_BASE_URL}/${mapSlug}_1_png.png`,
            iconUrl: `${MAP_IMAGE_BASE_URL}/${mapSlug}.png`,
        };
    }, [displayResult.map]);

    const rounds = useMemo(
        () => (Array.isArray(displayResult.rounds) ? displayResult.rounds : []),
        [displayResult.rounds]
    );

    const maxDamage = useMemo(
        () =>
            Math.max(
                ...displayResult.teams.flatMap((team) => team.participants.map((p) => p.damageDealt))
            ) || 0,
        [displayResult]
    );

    if (!team1 || !team2) {
        return null;
    }

    return (
        <table className="match-page__score-table mt">
            <tbody>
                <tr className="cs2-stats-row">
                    <td colSpan={4}>
                        <div
                            className="cs2-stats-row__content"
                            style={
                                mapArtwork
                                    ? ({
                                          '--cs2-map-image': `url(${mapArtwork.thumbnailUrl})`,
                                          '--cs2-map-fallback-image': `url(${mapArtwork.iconUrl})`,
                                      } as React.CSSProperties)
                                    : undefined
                            }
                        >
                            <div className="cs2-stats-row__content__game">
                                <div>
                                    {displayResult.map && <p>Map: {displayResult.map}</p>}
                                    {displayResult.roundsPlayed && <p>Rounds: {displayResult.roundsPlayed}</p>}
                                    {isUsingMockData && <p>Preview mode: showing local mock CS2 data.</p>}
                                </div>
                                <div className="cs2-stats-row__content__game__scoreline">
                                    <span>{team1.score}</span>
                                    <span className="cs2-stats-row__content__game__scoreline__divider">:</span>
                                    <span>{team2.score}</span>
                                </div>
                            </div>

                            <div className="cs2-stats-row__content__round-history">
                                <div className="cs2-stats-row__content__round-history__header">
                                    <h4>Round Timeline</h4>
                                    <div className="cs2-stats-row__content__round-history__legend">
                                        <span title="Elimination">☠</span>
                                        <span title="Defuse">🛡</span>
                                        <span title="Bomb exploded">💣</span>
                                        <span title="Time">⏱</span>
                                        <span title="Surrender">🏳</span>
                                    </div>
                                </div>

                                {rounds.length > 0 ? (
                                    <div className="cs2-stats-row__content__round-history__list">
                                        {rounds.map((round, index) => {
                                            const winningTeamId = round.winningTeamId || round.teamId;
                                            const winningTeam = winningTeamId ? teamById.get(winningTeamId) : undefined;
                                            const endType = round.endType || round.winType || round.reason || 'unknown';

                                            return (
                                                <div
                                                    className={classNames(
                                                        'cs2-stats-row__content__round-history__round',
                                                        {
                                                            'winner-team-1': winningTeam?.teamId === team1.teamId,
                                                            'winner-team-2': winningTeam?.teamId === team2.teamId,
                                                        }
                                                    )}
                                                    key={`${round.roundNumber || index}-${winningTeamId || 'unknown'}`}
                                                    title={`Round ${round.roundNumber || index + 1}: ${getRoundOutcomeLabel(endType)}`}
                                                >
                                                    <span className="round-index">{round.roundNumber || index + 1}</span>
                                                    <span className="round-outcome-icon">{getRoundOutcomeIcon(endType)}</span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <p className="cs2-stats-row__content__round-history__empty">
                                        Round-by-round end details are not in this result payload yet.
                                    </p>
                                )}
                            </div>

                            {displayResult.teams.map((team: Cs2MatchGameResultTeam) => (
                                <div
                                    className={classNames('cs2-stats-row__content__team', {
                                        winner: team.winner,
                                    })}
                                    key={team.teamId}
                                >
                                    <div className="cs2-stats-row__content__team__summary">
                                        <p>
                                            {team.winner && (
                                                <>
                                                    <span className="highlight">WINNER</span> -{' '}
                                                </>
                                            )}
                                            Score: {team.score}
                                        </p>
                                    </div>
                                    <table className="cs2-stats-row__content__team__participants">
                                        <thead>
                                            <tr>
                                                <th>Player</th>
                                                <th>K</th>
                                                <th>D</th>
                                                <th>A</th>
                                                <th>K/D</th>
                                                <th>ADR</th>
                                                <th>MVPs</th>
                                                <th>HS%</th>
                                                <th>Damage</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {sortBy(team.participants, (p) => -p.score).map(
                                                (participant: Cs2MatchGameResultParticipant) => {
                                                    const headshotPercentage =
                                                        participant.kills > 0
                                                            ? Math.round(
                                                                  (participant.killsWithHeadshot /
                                                                      participant.kills) *
                                                                      100
                                                              )
                                                            : 0;

                                                    return (
                                                        <tr
                                                            className="cs2-stats-row__content__team__participants__participant"
                                                            key={participant.steamId64}
                                                        >
                                                            <td>{participant.playerName}</td>
                                                            <td>{participant.kills}</td>
                                                            <td>{participant.deaths}</td>
                                                            <td>{participant.assists}</td>
                                                            <td>
                                                                {participant.kda.toFixed(2)}
                                                            </td>
                                                            <td>{participant.adr.toFixed(0)}</td>
                                                            <td>{participant.mvps}</td>
                                                            <td>{headshotPercentage}%</td>
                                                            <td>
                                                                <div className="damage-bar">
                                                                    <div
                                                                        className="damage-bar__fill"
                                                                        style={{
                                                                            width: `${
                                                                                maxDamage > 0
                                                                                    ? (participant.damageDealt /
                                                                                          maxDamage) *
                                                                                      100
                                                                                    : 0
                                                                            }%`,
                                                                        }}
                                                                    />
                                                                    <span className="damage-bar__text">
                                                                        {participant.damageDealt}
                                                                    </span>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    );
                                                }
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            ))}
                        </div>
                    </td>
                </tr>
            </tbody>
        </table>
    );
};

export default Cs2MatchGameResults;
